import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { extractNextUrlPath } from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead from '../components/SEOHead';
import './OurTeam.css';

const OurTeam = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch all pages if paginated
      let allTeamMembers = [];
      let nextUrl = '/accounts/team/';
      const langParam = { lang: i18n.language };
      
      while (nextUrl) {
        const response = await api.get(nextUrl, {
          params: nextUrl === '/accounts/team/' ? langParam : {}
        });
        
        // Handle paginated response or direct array
        const members = response.data.results || response.data || [];
        allTeamMembers = [...allTeamMembers, ...members];
        
        // Check if there's a next page
        if (response.data.next) {
          nextUrl = extractNextUrlPath(response.data.next);
        } else {
          nextUrl = null;
        }
      }
      
      setTeamMembers(allTeamMembers);
    } catch (err) {
      // If endpoint doesn't exist, set empty array (will show empty state)
      if (err.response?.status === 404) {
        setTeamMembers([]);
      } else {
        setError(t('team.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath.startsWith('/') ? '' : '/media/'}${imagePath}`;
  };

  const getFirstName = (member) => {
    // API returns language-aware 'first_name' field, fallback to English
    return member.first_name || member.first_name_en || '';
  };

  const getLastName = (member) => {
    // API returns language-aware 'last_name' field, fallback to English
    return member.last_name || member.last_name_en || '';
  };

  const getJobTitle = (member) => {
    // API returns language-aware 'job_title' field, fallback to English
    return member.job_title || member.job_title_en || '';
  };

  const getInitials = (member) => {
    const firstName = getFirstName(member);
    const lastName = getLastName(member);
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}` || '?';
  };

  const isCeoOrFounder = (member) => {
    const jobTitle = getJobTitle(member);
    const titleEn = member.position_details?.title_en || '';
    const titleKa = member.position_details?.title_ka || '';
    const combined = `${jobTitle} ${titleEn} ${titleKa}`.toLowerCase();
    return combined.includes('ceo') || combined.includes('founder');
  };

  const ceoFounders = teamMembers.filter(isCeoOrFounder);
  const restMembers = teamMembers.filter((m) => !isCeoOrFounder(m));

  const renderMemberCard = (member) => {
    const imageUrl = getImageUrl(member.photo || member.avatar);
    const firstName = getFirstName(member);
    const lastName = getLastName(member);
    const jobTitle = getJobTitle(member);
    const userId = member.id || member.user_id || member.user?.id;
    const isClickable = isAuthenticated() && userId;

    return (
      <div
        key={userId}
        className={`team-card ${isClickable ? 'team-card-clickable' : ''}`}
        onClick={() => {
          if (isClickable) {
            navigate(`/profile/${userId}`);
          }
        }}
        style={{ cursor: isClickable ? 'pointer' : 'default' }}
      >
        <div className="team-card-image-wrapper">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={`${firstName} ${lastName}`}
              className="team-card-image"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="team-card-initials"
            style={{ display: imageUrl ? 'none' : 'flex' }}
          >
            {getInitials(member)}
          </div>
        </div>
        <div className="team-card-info">
          <h3 className="team-card-name">
            {firstName} {lastName}
          </h3>
          {jobTitle && (
            <p className="team-card-title">{jobTitle}</p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="team-page">
          <div className="team-loading">
            <p>{t('team.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="team-page">
          <div className="team-error">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <SEOHead
        title="Our Team | GFM Studio"
        description="Meet the GFM Studio team of producers, engineers, and broadcast specialists."
        path="/team"
      />
      <div className="team-page">
        <div className="page-header">
          <h1>{i18n.language === 'ka' ? 'ჩვენი გუნდი' : 'Our Team'}</h1>
        </div>

        {teamMembers.length === 0 ? (
          <div className="team-empty">
            <p>{i18n.language === 'ka' ? 'გუნდის წევრები ჯერ არ არის დამატებული' : 'Team members will be added soon'}</p>
          </div>
        ) : (
          <>
            {ceoFounders.length > 0 && (
              <div className="team-leadership-section">
                <div className="team-grid">
                  {ceoFounders.map(renderMemberCard)}
                </div>
              </div>
            )}
            {ceoFounders.length > 0 && restMembers.length > 0 && (
              <div className="team-section-separator" />
            )}
            {restMembers.length > 0 && (
              <div className="team-rest-section">
                <div className="team-grid">
                  {restMembers.map(renderMemberCard)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OurTeam;

